import { UseFormReturn } from "react-hook-form";
import { hasTemplateVariables } from "@/lib/utils/crm/template-variables";
import { ComposeFormValues } from "@/lib/types/compose-modal-types";

// Type guard to ensure fieldName is a valid key of ComposeFormValues
function isValidFormField(fieldName: string): fieldName is keyof ComposeFormValues {
  const validFields: (keyof ComposeFormValues)[] = [
    "to",
    "cc",
    "bcc",
    "subject",
    "body",
    "fromAccount",
    "signature",
    "attachments",
  ];
  return validFields.includes(fieldName as keyof ComposeFormValues);
}

/**
 * Syncs template variables from inputValue to form field when detected.
 * This ensures template variables typed directly into the input are saved to the form.
 */
export function syncTemplateVariablesToForm(
  inputValue: string,
  form: UseFormReturn<ComposeFormValues>,
  fieldName: string
): void {
  if (!inputValue.trim()) return;
  if (!isValidFormField(fieldName)) return;

  // If inputValue contains template variables, sync it to the form field
  if (hasTemplateVariables(inputValue)) {
    const currentFormValue = form.getValues(fieldName);
    // Only process string fields for template variable sync
    const currentFormValueStr = typeof currentFormValue === "string" ? (currentFormValue || "").trim() : "";

    // Only update if the inputValue is different and contains template variables
    if (currentFormValueStr !== inputValue.trim()) {
      console.log("🔄 Syncing template variable from inputValue to form", {
        fieldName,
        inputValue,
        currentFormValue: currentFormValueStr,
      });
      form.setValue(fieldName, inputValue.trim());
    }
  }
}

/**
 * Updates form field with comma-separated email list, preserving template variables.
 * This is the critical logic that prevents overwriting template variables with email lists.
 */
export function updateFormFieldWithRecipients(
  selectedRecipients: Array<{ email: string }>,
  form: UseFormReturn<ComposeFormValues>,
  fieldName: string,
  inputValue: string
): void {
  if (!isValidFormField(fieldName)) return;

  const currentValue = form.getValues(fieldName) || "";
  const emailList = selectedRecipients.map((r) => r.email).join(", ");

  // Ensure currentValue and inputValue are strings for template variable checking
  const currentValueStr = typeof currentValue === "string" ? currentValue : "";
  const inputValueStr = typeof inputValue === "string" ? inputValue : "";

  // CRITICAL: ALWAYS check currentValue for template variables FIRST
  const hasTemplateVarsInForm = hasTemplateVariables(currentValueStr);
  const hasTemplateVarsInInput = hasTemplateVariables(inputValueStr);

  // Debug logging to track template variable preservation
  if (hasTemplateVarsInForm || hasTemplateVarsInInput) {
    console.log("🛡️ Template variables detected", {
      fieldName,
      currentValue: currentValueStr,
      inputValue: inputValueStr,
      emailList,
      selectedRecipientsCount: selectedRecipients.length,
      hasTemplateVarsInForm,
      hasTemplateVarsInInput,
    });
  }

  // CRITICAL: If the form field contains template variables, NEVER overwrite them
  if (hasTemplateVarsInForm) {
    // Form field has template variables - preserve them absolutely
    // Only sync inputValue to form if inputValue also has template variables and is different
    if (hasTemplateVarsInInput && inputValueStr.trim() && currentValueStr.trim() !== inputValueStr.trim()) {
      console.log("🛡️ Syncing inputValue with template variables to form", { fieldName });
      form.setValue(fieldName, inputValueStr.trim());
    }
    // Don't overwrite form field with emailList if form field has template variables
    return;
  }

  // Form field doesn't have template variables, but inputValue might
  if (hasTemplateVarsInInput) {
    // inputValue has template variables, sync it to form field
    if (currentValueStr.trim() !== inputValueStr.trim()) {
      console.log("🛡️ Syncing inputValue with template variables to form (no form vars)", {
        fieldName,
      });
      form.setValue(fieldName, inputValueStr.trim());
    }
    return;
  }

  // No template variables anywhere - safe to update normally.
  // Include any pending input (typed but not yet added as chip) so form "to" stays in sync and validation passes on submit.
  const pendingInput = (inputValueStr || "").trim();
  const effectiveValue = pendingInput
    ? (emailList ? `${emailList}, ${pendingInput}` : pendingInput)
    : emailList;
  if ((currentValueStr || "").trim() !== effectiveValue.trim()) {
    form.setValue(fieldName, effectiveValue);
  }
}
